/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { ExternalLink, FilePlus2, FileText, Upload } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '@/api';
import type { Declaration, DeclarationPurpose, DeclarationUpload, PaperReview, UploadDeclarationBody } from '@/api/areas/declarations';
import { Badge } from '@/components/Badge';
import { Callout, DataTable, Field, Section, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { KeyValue } from '@/components/KeyValue';
import { EmptyState, Loading } from '@/components/States';
import { useWrite, WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { formatDateTime } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { ActionDialog, FormActions } from '../compliance/ui';
import { CheckTable, DECLARATION_TONE, FileFrame, fileToBase64, ReadFileDialog, UPLOAD_TONE } from './shared';

function GenerateForm({ subjectId, onDone, onCancel }: { subjectId: string; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('declarations');
  const { t: tc } = useTranslation();
  const [domains, setDomains] = useState('');
  const [purpose, setPurpose] = useState<DeclarationPurpose>('SUBJECT_DECLARATION');
  const [returnReason, setReturnReason] = useState('');
  const write = useWrite((body: { domains: string[]; purpose: DeclarationPurpose; reason?: string }, opts) => api.generateDeclaration(subjectId, body, opts));
  const list = domains.split(/[\s,]+/).map((d) => d.trim()).filter(Boolean);
  const isReturn = purpose === 'RETURN';
  // An organisation's subject declaration names no domain (owner decision 2026-10-01).
  const isSubject = purpose === 'SUBJECT_DECLARATION';
  return (
    <form
      className="grid gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if ((isSubject || list.length) && (await write.run({ domains: isSubject ? [] : list, purpose, reason: isReturn ? returnReason.trim() : undefined }))) onDone();
      }}
    >
      <Field label={t('subject.purpose')} hint={t(`subject.purposeHint.${purpose}` as 'subject.purposeHint.REGISTRATION')}>
        <select
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value as DeclarationPurpose)}
          disabled={write.busy}
        >
          {(['SUBJECT_DECLARATION', 'REGISTRATION', 'PARENTAL_CONSENT', 'RETURN'] as const).map((p) => (
            <option key={p} value={p}>
              {t(`subject.purposes.${p}`)}
            </option>
          ))}
        </select>
      </Field>
      {isSubject ? null : (
        <Field label={t('subject.domains')} hint={t('subject.domainsHint')}>
          <Textarea rows={3} value={domains} onChange={(e) => setDomains(e.target.value)} disabled={write.busy} placeholder="vidu.vn" />
        </Field>
      )}
      {isReturn ? (
        <Field label={t('subject.returnReason')}>
          <Textarea rows={2} value={returnReason} onChange={(e) => setReturnReason(e.target.value)} disabled={write.busy} />
        </Field>
      ) : null}
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || (!isSubject && (!list.length || list.length > 50)) || (isReturn && (list.length !== 1 || !returnReason.trim()))}>
          <FilePlus2 /> {t('subject.generate')}
        </Button>
      </FormActions>
    </form>
  );
}

function UploadForm({ declaration, onDone, onCancel }: { declaration: Declaration; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('declarations');
  const { t: tc } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [reason, setReason] = useState('');
  const write = useWrite((body: UploadDeclarationBody, opts) => api.uploadDeclaration(declaration.declaration_id, body, opts));
  const type = file?.type === 'application/pdf' || file?.type === 'image/png' || file?.type === 'image/jpeg' ? file.type : null;
  return (
    <form
      className="grid gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!file || !type || !reason.trim()) return;
        const data = await fileToBase64(file);
        if (await write.run({ mime_type: type, data, reason: reason.trim() })) onDone();
      }}
    >
      <Callout tone="info">{t('subject.uploadHint')}</Callout>
      <Field label={t('subject.file')}>
        <Input type="file" accept="application/pdf,image/png,image/jpeg" onChange={(e) => setFile(e.target.files?.[0] ?? null)} disabled={write.busy} />
      </Field>
      <Field label={t('read.reason')}>
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} disabled={write.busy} />
      </Field>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !type || !reason.trim() || (file?.size ?? 0) > 20 * 1024 * 1024}>
          <Upload /> {t('subject.upload')}
        </Button>
      </FormActions>
    </form>
  );
}

/** One upload: status, kind, signer hints and the check table. */
export function UploadDetail({ u }: { u: DeclarationUpload }) {
  const { t } = useTranslation('declarations');
  const sig = u.signature;
  return (
    <div className="grid gap-3 rounded-md border p-3">
      <KeyValue
        columns={3}
        rows={[
          [t('subject.status'), <Badge key="s" tone={UPLOAD_TONE[u.status]} title={u.status}>{t(`uploadStatus.${u.status}`)}</Badge>],
          [t('subject.kind'), u.upload_kind ? t(`uploadKind.${u.upload_kind}`) : '—'],
          [t('subject.created'), formatDateTime(u.created_at)],
          sig ? [t('subject.signer'), `${sig.signer_hints.join(', ') || '—'}${sig.signed_as ? ` · ${t(`subject.signedAs.${sig.signed_as}`)}` : ''}`] : null,
          sig?.signing_time ? [t('subject.signingTime'), formatDateTime(sig.signing_time)] : null,
          sig ? [t('subject.revocation'), `${sig.revocation.status}${sig.revocation.source ? ` · ${sig.revocation.source}` : ''}${sig.revocation.reason ? ` · ${sig.revocation.reason}` : ''}`] : null,
          sig?.chain.length ? [t('subject.chain'), <span key="c" className="text-xs">{sig.chain.map((c) => c.subject_cn ?? c.issuer).join(' → ')}</span>] : null,
          u.work_item_id
            ? [
                t('subject.workItem'),
                <Link key="w" to={`/compliance/work/${encodeURIComponent(u.work_item_id)}`} className="inline-flex items-center gap-1 text-primary hover:underline">
                  {t('subject.workItem')} <ExternalLink className="size-3.5" aria-hidden="true" />
                </Link>,
              ]
            : null,
        ]}
      />
      {u.paper_review ? <PaperReport r={u.paper_review} /> : null}
      <CheckTable checks={u.checks} />
    </div>
  );
}

const PAPER_TONE = { MATCH: 'ok', MISMATCH: 'warn', REJECTED: 'crit', UNREADABLE: 'pending' } as const;

/**
 * A paper declaration's report, read on the server (owner decision 2026-10-01: local OCR, no
 * third-party AI): the status, the fields compared, the signature and the red stamp found.
 * Advice only — staff always decide.
 */
export function PaperReport({ r }: { r: PaperReview }) {
  const { t } = useTranslation('declarations');
  const pct = (x: number) => `${Math.round(x * 100)}%`;
  const mark = (m: { detected: boolean; confidence: number }) => (m.detected ? t('review.paper.detected', { confidence: pct(m.confidence) }) : t('review.paper.notDetected'));
  return (
    <div className="grid gap-2 rounded-md border p-3" data-paper-review={r.status}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{t('review.paper.title')}</span>
        <Badge tone={PAPER_TONE[r.status]} title={r.reason}>
          {t(`review.paper.status.${r.status}`)}
        </Badge>
      </div>
      <p className="text-sm">{r.message}</p>
      <KeyValue
        columns={3}
        rows={[
          [t('review.paper.fields', { matched: r.matched, compared: r.compared }), r.fields.filter((f) => f.status !== 'MATCH').map((f) => f.label ?? f.key).join(', ') || '—'],
          [t('review.paper.signature'), mark(r.signature)],
          [t('review.paper.stamp'), mark(r.stamp)],
          r.ocr ? [t('review.paper.engine'), `${r.engine} · ${t('review.paper.ocr', { lines: r.ocr.lines, confidence: r.ocr.mean_confidence === null ? '—' : pct(r.ocr.mean_confidence) })}`] : [t('review.paper.engine'), r.engine],
        ]}
      />
      <p className="text-xs text-muted-foreground">{t('review.paper.staffDecides')}</p>
    </div>
  );
}

/** The "Bản khai" tab of a subject: generate, download, upload, status and checks. */
export function SubjectDeclarations({ subjectId }: { subjectId: string }) {
  const { t } = useTranslation('declarations');
  const result = useApi((signal) => api.listSubjectDeclarations(subjectId, { signal }), [subjectId]);
  const [dialog, setDialog] = useState<{ kind: 'generate' } | { kind: 'upload'; d: Declaration } | null>(null);
  const [reading, setReading] = useState<{ title: string; load: (reason: string) => Promise<Blob> } | null>(null);
  const [file, setFile] = useState<{ title: string; blob: Blob } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const done = () => {
    setDialog(null);
    result.reload();
  };
  const columns: Column<Declaration>[] = [
    { key: 'created', header: t('subject.created'), className: 'whitespace-nowrap', cell: (d) => formatDateTime(d.created_at) },
    {
      key: 'domains',
      header: t('subject.domainsCol'),
      cell: (d) => (d.purpose === 'SUBJECT_DECLARATION' ? <Badge tone="info">{t('subject.purposes.SUBJECT_DECLARATION')}</Badge> : d.domains.join(', ')),
    },
    { key: 'template', header: t('subject.template'), cell: (d) => <code className="text-xs">{`${d.template}@${d.template_version}`}</code> },
    { key: 'status', header: t('subject.status'), cell: (d) => <Badge tone={DECLARATION_TONE[d.status]} title={d.status}>{t(`status.${d.status}`)}</Badge> },
    {
      key: 'actions',
      header: '',
      cell: (d) => (
        <span className="flex flex-wrap justify-end gap-1">
          <Button size="sm" variant="outline" onClick={() => setReading({ title: t('subject.download'), load: (reason) => api.declarationPdf(d.declaration_id, reason) })}>
            <FileText /> {t('subject.download')}
          </Button>
          {d.status !== 'VERIFIED' && d.status !== 'APPROVED' ? (
            <Button size="sm" variant="outline" onClick={() => setDialog({ kind: 'upload', d })}>
              <Upload /> {t('subject.upload')}
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => setOpen(open === d.declaration_id ? null : d.declaration_id)}>
            {t('subject.uploads')} ({d.uploads.length})
          </Button>
        </span>
      ),
    },
  ];
  const selected = result.status === 'success' ? result.data.declarations.find((d) => d.declaration_id === open) : undefined;
  return (
    <Section
      flush
      title={t('subject.title')}
      description={t('subject.description')}
      actions={
        <Button size="sm" onClick={() => setDialog({ kind: 'generate' })}>
          <FilePlus2 /> {t('subject.generate')}
        </Button>
      }
    >
      {result.status === 'loading' ? <Loading rows={3} /> : null}
      {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} /> : null}
      {result.status === 'success' ? (
        <DataTable
          columns={columns}
          rows={result.data.declarations}
          rowKey={(d) => d.declaration_id}
          empty={
            <EmptyState title={t('subject.empty')} icon={FileText}>
              {t('subject.emptyBody')}
            </EmptyState>
          }
        />
      ) : null}
      {selected ? (
        <div className="grid gap-3 p-5">
          <p className="text-sm font-medium">{t('subject.uploads')}</p>
          {selected.uploads.length ? (
            selected.uploads.map((u) => (
              <div key={u.submission_id} className="grid gap-2">
                <UploadDetail u={u} />
                <div>
                  <Button size="sm" variant="outline" onClick={() => setReading({ title: t('subject.viewFile'), load: (reason) => api.declarationUploadFile(u.submission_id, reason) })}>
                    <FileText /> {t('subject.viewFile')}
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">{t('subject.noUploads')}</p>
          )}
        </div>
      ) : null}
      <ActionDialog open={dialog?.kind === 'generate'} onOpenChange={(o) => (o ? undefined : setDialog(null))} title={t('subject.generateTitle')}>
        <GenerateForm subjectId={subjectId} onDone={done} onCancel={() => setDialog(null)} />
      </ActionDialog>
      <ActionDialog open={dialog?.kind === 'upload'} onOpenChange={(o) => (o ? undefined : setDialog(null))} title={t('subject.uploadTitle')}>
        {dialog?.kind === 'upload' ? <UploadForm declaration={dialog.d} onDone={done} onCancel={() => setDialog(null)} /> : null}
      </ActionDialog>
      <ReadFileDialog
        open={reading !== null}
        onOpenChange={(o) => (o ? undefined : setReading(null))}
        load={(reason) => (reading ? reading.load(reason) : Promise.reject(new Error('no file')))}
        onLoaded={(blob) => setFile({ title: reading?.title ?? '', blob })}
      />
      <ActionDialog open={file !== null} onOpenChange={(o) => (o ? undefined : setFile(null))} title={file?.title ?? ''} wide>
        <FileFrame blob={file?.blob ?? null} title={file?.title ?? ''} />
      </ActionDialog>
    </Section>
  );
}
